import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Bell,
  MessageSquare,
  Shield,
  User,
  Lock,
  Monitor,
  Smartphone,
  Tablet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Phone,
} from 'lucide-react';

const TABS = [
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'sms', label: 'SMS', icon: MessageSquare },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'account', label: 'Account', icon: User },
];

// ---------------------------------------------------------------------------
// Notifications Tab
// ---------------------------------------------------------------------------

const INITIAL_EMAIL_TOGGLES = [
  {
    id: 'service-requests',
    label: 'Service request updates',
    description: 'Get notified when a service request is created, updated, or completed.',
    enabled: true,
  },
  {
    id: 'new-messages',
    label: 'New messages',
    description: 'In-app messaging alerts when you receive a new message.',
    enabled: true,
  },
  {
    id: 'lease-documents',
    label: 'Lease & document activity',
    description: 'Updates on lease agreements, document uploads, and signatures.',
    enabled: false,
  },
  {
    id: 'billing-payments',
    label: 'Billing & payment events',
    description: 'Payment confirmations, rent reminders, and billing updates.',
    enabled: true,
  },
  {
    id: 'product-updates',
    label: 'Product updates / announcements',
    description: 'News about ZenRent features, tips, and platform updates.',
    enabled: false,
  },
];

function NotificationsTab() {
  const [toggles, setToggles] = useState(INITIAL_EMAIL_TOGGLES);
  const [frequency, setFrequency] = useState('realtime');

  const handleToggle = (id) => {
    setToggles((prev) =>
      prev.map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t))
    );
  };

  const handleReset = () => {
    setToggles(INITIAL_EMAIL_TOGGLES);
    setFrequency('realtime');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
      <h3 className="text-[#2C3E50]" style={{ fontSize: '18px', fontWeight: 600 }}>
        Email Preferences
      </h3>
      <p className="text-[#7F8C8D] text-sm mt-1">
        Choose which emails ZenRent sends you.
      </p>

      <div className="mt-6 space-y-4">
        {toggles.map((toggle) => (
          <div
            key={toggle.id}
            className="flex items-start justify-between gap-4 py-3 border-b border-[#E2E8F0] last:border-b-0"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                {toggle.label}
              </p>
              <p className="text-xs text-[#7F8C8D] mt-0.5">{toggle.description}</p>
            </div>
            <Switch
              checked={toggle.enabled}
              onCheckedChange={() => handleToggle(toggle.id)}
              className="data-[state=checked]:bg-[#2ECC71] mt-0.5"
            />
          </div>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
        <label className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
          Email frequency
        </label>
        <div className="mt-2 max-w-[260px]">
          <Select value={frequency} onValueChange={setFrequency}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="realtime">Real-time</SelectItem>
              <SelectItem value="daily">Daily digest</SelectItem>
              <SelectItem value="weekly">Weekly digest</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-[#E2E8F0]">
        <button
          onClick={handleReset}
          className="px-5 py-2 text-sm rounded-lg border border-[#DDDDDD] text-[#7F8C8D] bg-white hover:bg-gray-50 transition-colors"
          style={{ fontWeight: 500 }}
        >
          Cancel / Reset
        </button>
        <button
          className="px-5 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors"
          style={{ fontWeight: 500 }}
        >
          Save
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SMS Tab
// ---------------------------------------------------------------------------

function SmsTab() {
  const [twoFactor, setTwoFactor] = useState(true);
  const [loginAlerts, setLoginAlerts] = useState(true);
  const [criticalNotifs, setCriticalNotifs] = useState(true);
  const [countryCode, setCountryCode] = useState('us');
  const [phoneNumber, setPhoneNumber] = useState('(555) 123-4567');
  const [phoneVerified, setPhoneVerified] = useState(true);

  const [rentalMessages, setRentalMessages] = useState(true);
  const [serviceUpdates, setServiceUpdates] = useState(true);
  const [appointmentReminders, setAppointmentReminders] = useState(false);
  const [paymentReminders, setPaymentReminders] = useState(true);
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState('21:00');
  const [quietEnd, setQuietEnd] = useState('07:00');
  const [smsFrequency, setSmsFrequency] = useState('immediate');

  return (
    <div className="space-y-6">
      {/* Transactional SMS */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
        <div className="flex items-start gap-3 mb-1">
          <Shield className="w-5 h-5 text-[#3498DB] mt-0.5 shrink-0" />
          <div>
            <h3 className="text-[#2C3E50]" style={{ fontSize: '18px', fontWeight: 600 }}>
              Transactional SMS
            </h3>
            <p className="text-[#7F8C8D] text-sm mt-1">
              Security and critical account notifications sent by ZenRent (ex: 2FA, login alerts).
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div className="flex items-start justify-between gap-4 py-3 border-b border-[#E2E8F0]">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                  Two-factor authentication codes
                </p>
                <span className="text-[10px] px-1.5 py-0.5 bg-[#2ECC71]/10 text-[#27AE60] rounded" style={{ fontWeight: 500 }}>
                  Recommended
                </span>
              </div>
              <p className="text-xs text-[#7F8C8D] mt-0.5">
                Receive verification codes via SMS for secure login.
              </p>
            </div>
            <Switch
              checked={twoFactor}
              onCheckedChange={setTwoFactor}
              className="data-[state=checked]:bg-[#2ECC71] mt-0.5"
            />
          </div>

          <div className="flex items-start justify-between gap-4 py-3 border-b border-[#E2E8F0]">
            <div className="flex-1">
              <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                Login / security alerts
              </p>
              <p className="text-xs text-[#7F8C8D] mt-0.5">
                Get notified about new sign-ins and security events.
              </p>
            </div>
            <Switch
              checked={loginAlerts}
              onCheckedChange={setLoginAlerts}
              className="data-[state=checked]:bg-[#2ECC71] mt-0.5"
            />
          </div>

          <div className="flex items-start justify-between gap-4 py-3 border-b border-[#E2E8F0]">
            <div className="flex-1">
              <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                Critical account notifications
              </p>
              <p className="text-xs text-[#7F8C8D] mt-0.5">
                Important alerts about your account status and changes.
              </p>
            </div>
            <Switch
              checked={criticalNotifs}
              onCheckedChange={setCriticalNotifs}
              className="data-[state=checked]:bg-[#2ECC71] mt-0.5"
            />
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
          <label className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
            Phone number
          </label>
          <div className="flex items-center gap-3 mt-2">
            <div className="w-[100px]">
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="us">+1</SelectItem>
                  <SelectItem value="uk">+44</SelectItem>
                  <SelectItem value="ca">+1</SelectItem>
                  <SelectItem value="au">+61</SelectItem>
                  <SelectItem value="de">+49</SelectItem>
                  <SelectItem value="fr">+33</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 max-w-[220px]">
              <Input
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="(555) 000-0000"
              />
            </div>
            {phoneVerified ? (
              <div className="flex items-center gap-1.5 text-[#2ECC71]">
                <CheckCircle className="w-4 h-4" />
                <span className="text-xs" style={{ fontWeight: 500 }}>Verified</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-[#E74C3C]">
                <Phone className="w-4 h-4" />
                <span className="text-xs" style={{ fontWeight: 500 }}>Not verified</span>
              </div>
            )}
          </div>
          {phoneVerified ? (
            <button
              className="text-xs text-[#3498DB] hover:underline mt-2"
              onClick={() => setPhoneVerified(false)}
            >
              Change number
            </button>
          ) : (
            <button
              className="mt-2 px-4 py-1.5 text-xs rounded-lg border border-[#DDDDDD] text-[#1E293B] bg-white hover:bg-gray-50 transition-colors"
              style={{ fontWeight: 500 }}
              onClick={() => setPhoneVerified(true)}
            >
              Verify number
            </button>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-[#E2E8F0]">
          <button
            className="px-5 py-2 text-sm rounded-lg border border-[#DDDDDD] text-[#7F8C8D] bg-white hover:bg-gray-50 transition-colors"
            style={{ fontWeight: 500 }}
          >
            Send test SMS
          </button>
          <button
            className="px-5 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors"
            style={{ fontWeight: 500 }}
          >
            Save Transactional SMS Settings
          </button>
        </div>
      </div>

      {/* Rental Communications */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
        <div className="flex items-start gap-3 mb-1">
          <Phone className="w-5 h-5 text-[#2ECC71] mt-0.5 shrink-0" />
          <div>
            <h3 className="text-[#2C3E50]" style={{ fontSize: '18px', fontWeight: 600 }}>
              Rental Communications
            </h3>
            <p className="text-[#7F8C8D] text-sm mt-1">
              Text message notifications related to rentals and communication (messages, service requests, scheduling).
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div className="flex items-start justify-between gap-4 py-3 border-b border-[#E2E8F0]">
            <div className="flex-1">
              <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                New rental message notifications
              </p>
              <p className="text-xs text-[#7F8C8D] mt-0.5">
                Get a text when someone sends you a rental-related message.
              </p>
            </div>
            <Switch
              checked={rentalMessages}
              onCheckedChange={setRentalMessages}
              className="data-[state=checked]:bg-[#2ECC71] mt-0.5"
            />
          </div>

          <div className="flex items-start justify-between gap-4 py-3 border-b border-[#E2E8F0]">
            <div className="flex-1">
              <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                Service request updates
              </p>
              <p className="text-xs text-[#7F8C8D] mt-0.5">
                SMS alerts when a service request status changes.
              </p>
            </div>
            <Switch
              checked={serviceUpdates}
              onCheckedChange={setServiceUpdates}
              className="data-[state=checked]:bg-[#2ECC71] mt-0.5"
            />
          </div>

          <div className="flex items-start justify-between gap-4 py-3 border-b border-[#E2E8F0]">
            <div className="flex-1">
              <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                Appointment reminders
              </p>
              <p className="text-xs text-[#7F8C8D] mt-0.5">
                Reminders for scheduled inspections, viewings, or maintenance visits.
              </p>
            </div>
            <Switch
              checked={appointmentReminders}
              onCheckedChange={setAppointmentReminders}
              className="data-[state=checked]:bg-[#2ECC71] mt-0.5"
            />
          </div>

          <div className="flex items-start justify-between gap-4 py-3 border-b border-[#E2E8F0]">
            <div className="flex-1">
              <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                Payment / rent reminders
              </p>
              <p className="text-xs text-[#7F8C8D] mt-0.5">
                Upcoming payment due dates and rent reminders.
              </p>
            </div>
            <Switch
              checked={paymentReminders}
              onCheckedChange={setPaymentReminders}
              className="data-[state=checked]:bg-[#2ECC71] mt-0.5"
            />
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                Enable quiet hours
              </p>
              <p className="text-xs text-[#7F8C8D] mt-0.5">
                Silence non-critical SMS during specific hours.
              </p>
            </div>
            <Switch
              checked={quietHoursEnabled}
              onCheckedChange={setQuietHoursEnabled}
              className="data-[state=checked]:bg-[#2ECC71]"
            />
          </div>

          {quietHoursEnabled && (
            <div className="flex items-center gap-3 mt-3">
              <div className="flex items-center gap-2">
                <label className="text-xs text-[#7F8C8D]">From</label>
                <input
                  type="time"
                  value={quietStart}
                  onChange={(e) => setQuietStart(e.target.value)}
                  className="border border-[#DDDDDD] rounded-md px-2 py-1.5 text-sm bg-[#F9F9F9]"
                />
              </div>
              <span className="text-xs text-[#7F8C8D]">to</span>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={quietEnd}
                  onChange={(e) => setQuietEnd(e.target.value)}
                  className="border border-[#DDDDDD] rounded-md px-2 py-1.5 text-sm bg-[#F9F9F9]"
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
          <label className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
            SMS frequency
          </label>
          <div className="mt-2 max-w-[260px]">
            <Select value={smsFrequency} onValueChange={setSmsFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Immediate</SelectItem>
                <SelectItem value="bundled">Bundled (15 min)</SelectItem>
                <SelectItem value="hourly">Hourly digest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-[#E2E8F0]">
          <button
            className="px-5 py-2 text-sm rounded-lg border border-[#DDDDDD] text-[#7F8C8D] bg-white hover:bg-gray-50 transition-colors"
            style={{ fontWeight: 500 }}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors"
            style={{ fontWeight: 500 }}
          >
            Save Rental Communication Settings
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Security Tab
// ---------------------------------------------------------------------------

const PLACEHOLDER_SESSIONS = [
  { id: '1', device: 'Chrome on macOS', icon: Monitor, lastActive: 'Active now', current: true },
  { id: '2', device: 'Safari on iPhone 15', icon: Smartphone, lastActive: '2 hours ago', current: false },
  { id: '3', device: 'Chrome on iPad', icon: Tablet, lastActive: '3 days ago', current: false },
];

function SecurityTab() {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const requirements = [
    { label: 'At least 8 characters', met: newPassword.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(newPassword) },
    { label: 'One lowercase letter', met: /[a-z]/.test(newPassword) },
    { label: 'One number', met: /[0-9]/.test(newPassword) },
    { label: 'One special character (!@#$%...)', met: /[^A-Za-z0-9]/.test(newPassword) },
  ];

  const metCount = requirements.filter((r) => r.met).length;
  const strengthPercent = (metCount / requirements.length) * 100;
  const strengthLabel =
    metCount === 0 ? '' : metCount <= 2 ? 'Weak' : metCount <= 4 ? 'Fair' : 'Strong';
  const strengthColor =
    metCount <= 2 ? '#E74C3C' : metCount <= 4 ? '#F39C12' : '#2ECC71';

  const passwordMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  return (
    <div className="space-y-6">
      {/* Password & Sign-in */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-5 h-5 text-[#3498DB]" />
          <h3 className="text-[#2C3E50]" style={{ fontSize: '18px', fontWeight: 600 }}>
            Password & Sign-in
          </h3>
        </div>
        <p className="text-[#7F8C8D] text-sm mt-1 mb-6">
          Manage your password and sign-in methods.
        </p>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setChangePasswordOpen(!changePasswordOpen)}
            className="px-5 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors"
            style={{ fontWeight: 500 }}
          >
            {changePasswordOpen ? 'Cancel' : 'Change password'}
          </button>
          <button
            className="px-5 py-2 text-sm rounded-lg border border-[#DDDDDD] text-[#1E293B] bg-white hover:bg-gray-50 transition-colors"
            style={{ fontWeight: 500 }}
          >
            Send password reset email
          </button>
        </div>

        {changePasswordOpen && (
          <div className="mt-6 pt-4 border-t border-[#E2E8F0] space-y-4 max-w-md">
            <div>
              <label className="text-sm text-[#1E293B] mb-1.5 block" style={{ fontWeight: 500 }}>
                Current password
              </label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label className="text-sm text-[#1E293B] mb-1.5 block" style={{ fontWeight: 500 }}>
                New password
              </label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />

              {newPassword.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-[#7F8C8D]">Password strength</span>
                    <span className="text-xs" style={{ color: strengthColor, fontWeight: 500 }}>
                      {strengthLabel}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${strengthPercent}%`, backgroundColor: strengthColor }}
                    />
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {requirements.map((req) => (
                      <li key={req.label} className="flex items-center gap-2 text-xs">
                        {req.met ? (
                          <CheckCircle className="w-3.5 h-3.5 text-[#2ECC71]" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-[#DDDDDD]" />
                        )}
                        <span className={req.met ? 'text-[#2ECC71]' : 'text-[#7F8C8D]'}>
                          {req.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-[#1E293B] mb-1.5 block" style={{ fontWeight: 500 }}>
                Confirm new password
              </label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className={passwordMismatch ? 'border-[#E74C3C]' : ''}
              />
              {passwordMismatch && (
                <p className="flex items-center gap-1 text-xs text-[#E74C3C] mt-1.5">
                  <AlertTriangle className="w-3 h-3" />
                  Passwords do not match
                </p>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <button
                disabled={
                  !currentPassword || !newPassword || !confirmPassword || passwordMismatch || metCount < 5
                }
                className="px-5 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontWeight: 500 }}
              >
                Update password
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Active Sessions */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
        <h3 className="text-[#2C3E50] mb-1" style={{ fontSize: '18px', fontWeight: 600 }}>
          Active Sessions
        </h3>
        <p className="text-[#7F8C8D] text-sm mb-6">
          Devices and browsers currently signed into your account.
        </p>

        <div className="space-y-3">
          {PLACEHOLDER_SESSIONS.map((session) => {
            const SessionIcon = session.icon;
            return (
              <div
                key={session.id}
                className="flex items-center justify-between py-3 px-4 rounded-lg border border-[#E2E8F0] bg-[#F9F9F9]"
              >
                <div className="flex items-center gap-3">
                  <SessionIcon className="w-5 h-5 text-[#7F8C8D]" />
                  <div>
                    <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                      {session.device}
                      {session.current && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-[#2ECC71]/10 text-[#27AE60] rounded" style={{ fontWeight: 500 }}>
                          This device
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[#7F8C8D]">{session.lastActive}</p>
                  </div>
                </div>
                {!session.current && (
                  <button
                    className="text-xs text-[#E74C3C] hover:text-[#C0392B] transition-colors px-3 py-1.5 border border-[#E74C3C]/20 rounded-md hover:bg-[#E74C3C]/5"
                    style={{ fontWeight: 500 }}
                  >
                    Sign out
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-end mt-6 pt-4 border-t border-[#E2E8F0]">
          <button
            className="px-5 py-2 text-sm rounded-lg bg-[#E74C3C] text-white hover:bg-[#C0392B] transition-colors"
            style={{ fontWeight: 500 }}
          >
            Sign out of all devices
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account Tab
// ---------------------------------------------------------------------------

const TIMEZONES = [
  { value: 'america-new-york', label: 'Eastern Time (US & Canada)' },
  { value: 'america-chicago', label: 'Central Time (US & Canada)' },
  { value: 'america-denver', label: 'Mountain Time (US & Canada)' },
  { value: 'america-los-angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'america-anchorage', label: 'Alaska' },
  { value: 'pacific-honolulu', label: 'Hawaii' },
  { value: 'europe-london', label: 'London (GMT)' },
  { value: 'europe-paris', label: 'Paris (CET)' },
  { value: 'asia-tokyo', label: 'Tokyo (JST)' },
  { value: 'australia-sydney', label: 'Sydney (AEST)' },
];

function AccountTab() {
  const [name, setName] = useState('Test User');
  const [timezone, setTimezone] = useState('america-new-york');
  const [contactMethod, setContactMethod] = useState('email');
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  return (
    <div className="space-y-6">
      {/* Profile */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
        <div className="flex items-center gap-2 mb-1">
          <User className="w-5 h-5 text-[#3498DB]" />
          <h3 className="text-[#2C3E50]" style={{ fontSize: '18px', fontWeight: 600 }}>
            Profile
          </h3>
        </div>
        <p className="text-[#7F8C8D] text-sm mt-1 mb-6">
          Your personal information and preferences.
        </p>

        <div className="space-y-5 max-w-lg">
          <div>
            <label className="text-sm text-[#1E293B] mb-1.5 block" style={{ fontWeight: 500 }}>
              Name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
            />
          </div>

          <div>
            <label className="text-sm text-[#1E293B] mb-1.5 flex items-center gap-1.5" style={{ fontWeight: 500 }}>
              Email
              <Lock className="w-3 h-3 text-[#7F8C8D]" />
            </label>
            <Input
              value="test@zenrent.com"
              readOnly
              className="bg-[#F1F5F9] text-[#64748B] cursor-not-allowed"
            />
            <p className="text-xs text-[#7F8C8D] mt-1">Email cannot be changed.</p>
          </div>

          <div>
            <label className="text-sm text-[#1E293B] mb-1.5 block" style={{ fontWeight: 500 }}>
              Timezone
            </label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-[#1E293B] mb-2 block" style={{ fontWeight: 500 }}>
              Preferred contact method
            </label>
            <RadioGroup value={contactMethod} onValueChange={setContactMethod} className="flex gap-6">
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="email"
                  id="contact-email"
                  className="border-[#DDDDDD] data-[state=checked]:border-[#2ECC71] text-[#2ECC71]"
                />
                <label htmlFor="contact-email" className="text-sm text-[#1E293B] cursor-pointer">
                  Email
                </label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="sms"
                  id="contact-sms"
                  className="border-[#DDDDDD] data-[state=checked]:border-[#2ECC71] text-[#2ECC71]"
                />
                <label htmlFor="contact-sms" className="text-sm text-[#1E293B] cursor-pointer">
                  SMS
                </label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-[#E2E8F0]">
          <button
            className="px-5 py-2 text-sm rounded-lg border border-[#DDDDDD] text-[#7F8C8D] bg-white hover:bg-gray-50 transition-colors"
            style={{ fontWeight: 500 }}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors"
            style={{ fontWeight: 500 }}
          >
            Save Profile
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E74C3C]/20 p-6">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-5 h-5 text-[#E74C3C]" />
          <h3 className="text-[#E74C3C]" style={{ fontSize: '18px', fontWeight: 600 }}>
            Danger Zone
          </h3>
        </div>
        <p className="text-[#7F8C8D] text-sm mt-1 mb-6">
          Irreversible actions. Please proceed with caution.
        </p>

        <Dialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
          <DialogTrigger asChild>
            <button
              className="px-5 py-2 text-sm rounded-lg bg-[#E74C3C] text-white hover:bg-[#C0392B] transition-colors"
              style={{ fontWeight: 500 }}
            >
              Deactivate account
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#E74C3C]" />
                Deactivate your account?
              </DialogTitle>
              <DialogDescription>
                This action will deactivate your ZenRent account. You will lose access to all
                properties, messages, and data. This action may be irreversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button
                onClick={() => setDeactivateOpen(false)}
                className="px-5 py-2 text-sm rounded-lg border border-[#DDDDDD] text-[#7F8C8D] bg-white hover:bg-gray-50 transition-colors"
                style={{ fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                onClick={() => setDeactivateOpen(false)}
                className="px-5 py-2 text-sm rounded-lg bg-[#E74C3C] text-white hover:bg-[#C0392B] transition-colors"
                style={{ fontWeight: 500 }}
              >
                Yes, deactivate
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings Page (main)
// ---------------------------------------------------------------------------

function SettingsDemo() {
  const [activeTab, setActiveTab] = useState('notifications');

  return (
    <Layout pageTitle="Settings" pageSubtitle="Manage notifications, messaging, and security.">
      {/* Tab navigation */}
      <div className="flex gap-1 mb-6 bg-white rounded-lg border border-[#E2E8F0] p-1 w-fit shadow-sm">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-[#2ECC71] text-white shadow-sm'
                  : 'text-[#7F8C8D] hover:text-[#2C3E50] hover:bg-[#F1F5F9]'
              }`}
              style={{ fontWeight: activeTab === tab.id ? 500 : 400 }}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="max-w-3xl">
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'sms' && <SmsTab />}
        {activeTab === 'security' && <SecurityTab />}
        {activeTab === 'account' && <AccountTab />}
      </div>
    </Layout>
  );
}

export default SettingsDemo;
